import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import styled from 'styled-components';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { getVehicleSearchHistory } from 'domains/user';
import { SearchHistoryRow } from 'pages/CatalogPage/DH/PopoverContent/SearchHistoryRow';
import { SearchVariantOption, SearchVariantType } from 'pages/HomePage/search/SearchBar';
import { SearchByModel } from 'pages/HomePage/search/SearchByModel';
import { SearchByVINVRN } from 'pages/HomePage/search/SearchByVINVRN';

const SIdentifyContent = styled.div.attrs<{ $wide?: boolean }>(({ $wide }) => ({
  'data-testid': 'identify-new-vehicle-content',
  'data-wide': $wide ? 'true' : 'false',
}))<{ $wide?: boolean }>`
  width: ${({ $wide }) => ($wide ? 'min(680px, 90vw)' : '520px')};
  max-height: 80vh;
  padding: 16px 16px 16px 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const STabRow = styled.div`
  display: flex;
  border: 1px solid ${({ theme: t }) => t.color.grey85};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const STabButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  height: 42px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? '700' : '400')};
  background: ${({ $active, theme: t }) => ($active ? t.color.white : t.color.grey95)};
  color: ${({ theme: t }) => t.color.grey15};
  letter-spacing: 0.5px;

  &:hover {
    background: ${({ $active, theme: t }) => ($active ? t.color.white : t.color.grey90)};
  }
`;

const SIdentifyTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: ${({ theme: t }) => t.color.grey15};
  margin-bottom: 8px;
`;

const SIdentifyDescription = styled.div`
  font-size: 14px;
  color: ${({ theme: t }) => t.color.grey45};
  margin-bottom: 20px;
  line-height: 1.5;
`;

const SRecentVehiclesLabel = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme: t }) => t.color.grey15};
  margin-top: 20px;
  margin-bottom: 8px;
  padding: 0 14px;
`;

const SRecentVehiclesList = styled.div`
  padding: 0 2px;
`;

export interface IdentifyNewVehicleContentProps {
  visible?: boolean;
  setIsChangeVehiclePopupVisible: (a: boolean) => void;
  onFound?: (vehicleDetail: VehicleDetail) => void;
  setIsChangeVehicle?: (b: boolean) => void;
}

export function IdentifyNewVehicleContent({
  visible,
  setIsChangeVehiclePopupVisible,
  onFound,
  setIsChangeVehicle,
}: Readonly<IdentifyNewVehicleContentProps>) {
  const { t } = useTranslation();
  const vehicleSearchHistory = useSelector(getVehicleSearchHistory);
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const [activeTab, setActiveTab] = useState<'VINVRN' | 'MODEL'>('VINVRN');

  const searchVariantOptions: SearchVariantOption[] = useMemo(
    () => [
      { title: t('catalog.parts.search.by_vin_or_vrn', 'Search by VIN or VRN'), value: 'VINVRN' },
      { title: t('catalog.parts.search.by_model', 'Search by vehicle model'), value: 'Model' },
    ],
    [t],
  );

  const handleSetSearchVariant = useCallback((variant: SearchVariantType) => {
    setActiveTab(variant === 'Model' ? 'MODEL' : 'VINVRN');
  }, []);

  const handleVinVrnTabClick = useCallback(() => {
    setActiveTab('VINVRN');
  }, []);

  const handleModelTabClick = useCallback(() => {
    setActiveTab('MODEL');
  }, []);

  const recentVehicles = useMemo(() => {
    const all = vehicleSearchHistory.data ?? [];
    return all.filter((v) => v.vehicleKey !== lastSearchedVehicleKey).slice(0, 2);
  }, [vehicleSearchHistory.data, lastSearchedVehicleKey]);

  useEffect(() => {
    return () => {
      if (setIsChangeVehicle) {
        setIsChangeVehicle(true);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SIdentifyContent $wide={activeTab === 'MODEL'}>
      <STabRow>
        <STabButton type={'button'} $active={activeTab === 'VINVRN'} onClick={handleVinVrnTabClick}>
          {t('catalog.search.tab.vin_vrn', 'VIN or VRN')}
        </STabButton>
        <STabButton type={'button'} $active={activeTab === 'MODEL'} onClick={handleModelTabClick}>
          {t('catalog.search.tab.brand_model', 'BRAND and MODEL')}
        </STabButton>
      </STabRow>
      <SIdentifyTitle>
        {activeTab === 'MODEL'
          ? t('catalog.search.by_brand_model.title', 'Search by Brand & Model')
          : t('catalog.search.enter_vehicle_identification', 'Enter vehicle identification')}
      </SIdentifyTitle>
      {activeTab === 'MODEL' && (
        <SIdentifyDescription>
          {t(
            'catalog.search.by_brand_model.description',
            "Don't have the VIN or plate? No problem, select your vehicle details below.",
          )}
        </SIdentifyDescription>
      )}
      {activeTab === 'VINVRN' && (
        <SIdentifyDescription>
          {t(
            'catalog.search.enter_vehicle_identification.description',
            // eslint-disable-next-line max-len
            "First select vehicle's country, then fill vehicle identification number or Vehicle's plate.",
          )}
        </SIdentifyDescription>
      )}
      {activeTab === 'VINVRN' && (
        <SearchByVINVRN
          hideVariantSelector
          darkSearchButton
          showMicrophoneIcon
          inputPlaceholder={t('catalog.search.vin_vrn.placeholder', 'AB-123_CD or VF1RFE00168106307')}
          visible={visible}
          searchVariant={'VINVRN'}
          setSearchVariant={handleSetSearchVariant}
          searchVariantOptions={searchVariantOptions}
          onFound={onFound}
        />
      )}
      {activeTab === 'MODEL' && (
        <SearchByModel
          hideVariantSelector
          plain
          searchVariant={'Model'}
          setSearchVariant={handleSetSearchVariant}
          searchVariantOptions={searchVariantOptions}
          setIsChangeVehiclePopupVisible={setIsChangeVehiclePopupVisible}
          onFound={onFound}
        />
      )}
      {activeTab === 'VINVRN' && recentVehicles.length > 0 && (
        <>
          <SRecentVehiclesLabel>{t('catalog.search.recent_vehicles', 'Recent Vehicles')}</SRecentVehiclesLabel>
          <SRecentVehiclesList>
            {recentVehicles.map((vehicle) => (
              <SearchHistoryRow key={vehicle.vehicleKey} vehicle={vehicle} />
            ))}
          </SRecentVehiclesList>
        </>
      )}
    </SIdentifyContent>
  );
}
